import p5Types from "p5";
import { splConstants } from "@malloc-protocol/spl";
import { SOURCE_NAME } from "../config/constants";
import {
  RED_FAIL,
  GREEN_SUCCESS,
  LIGHT_1,
  GREEN_1,
  GREEN_1_transparent,
  LIGHT_2,
  DARK_2,
} from "./colors";

export const vectorAdd = (a: number[], b: number[]) => {
  return [a[0] + b[0], a[1] + b[1]];
};

export const vectorSub = (a: number[], b: number[]) => {
  return [a[0] - b[0], a[1] - b[1]];
};

export const vectorScale = (a: number[], s: number) => {
  return [a[0] * s, a[1] * s];
};

export const vectorMag = (a: number[]): number => {
  return Math.sqrt(a[0] * a[0] + a[1] * a[1]);
};

export const dist = (x1: number, y1: number, x2: number, y2: number) => {
  return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
};

export const vectorHeading = (a: number[]) => {
  return Math.atan2(a[1], a[0]);
};

export class Node {
  pos: number[];
  vel: number[];
  acc: number[];
  actionType: any;
  r: number; // radius of circle
  r2: number; // radius of influence for mouseDrag
  r3: number; // radius of influence for force direction
  hover: boolean;
  hover2: boolean;
  id: string;
  selected: boolean;
  successfullyRan: boolean;
  failedRunning: boolean;
  isSource: boolean;
  outEdges: { [actionId: string]: Edge };

  constructor(
    id: string,
    pos = [0, 0],
    isSource = false,
    selected = false,
    type = null
  ) {
    this.pos = pos;
    this.vel = [0, 0];
    this.acc = [0, 0];
    this.r = 15;
    this.r2 = 2 * this.r;
    this.r3 = this.r * 4;
    this.hover = false;
    this.hover2 = false;
    this.actionType = type;
    this.id = id;
    this.selected = selected;
    this.isSource = isSource;
    this.failedRunning = false;
    this.successfullyRan = false;
    this.outEdges = {};
  }

  updateHover(x: number, y: number) {
    this.hover = dist(x, y, this.pos[0], this.pos[1]) < this.r;
    this.hover2 = dist(x, y, this.pos[0], this.pos[1]) < this.r2;
  }

  repel(p: Node) {
    let dir = vectorSub(this.pos, p.pos);
    let d = vectorMag(dir);
    dir = vectorScale(dir, 1 / d);

    if (d < this.r3) {
      let force = -1 / (d * d);
      p.acc = vectorAdd(p.acc, vectorScale(dir, force));
    } else {
      p.acc = [0, 0];
    }
  }

  draw(p5: p5Types) {
    p5.noStroke();
    // TODO: @Shiva please style lmao

    p5.fill(LIGHT_1);
    p5.circle(this.pos[0], this.pos[1], 2 * this.r);

    if (this.selected) {
      p5.stroke(GREEN_1);
      p5.strokeWeight(3);
      p5.noFill();
      p5.circle(this.pos[0], this.pos[1], 2 * this.r * 1.5);
    }

    if (this.failedRunning) {
      p5.fill(RED_FAIL);
    } else if (this.successfullyRan) {
      p5.fill(GREEN_1);
    } else if (this.isSource) {
      p5.fill(DARK_2);
    } else {
      p5.fill(LIGHT_2);
    }
    p5.noStroke();
    p5.circle(this.pos[0], this.pos[1], 2 * this.r * 0.7);

    if (this.hover) {
      p5.fill(LIGHT_1);
      // p5.textFont(
      //   p5.loadFont(
      //     "./src/fonts/Open_Sans/static/OpenSans/OpenSans-LightItalic.ttf"
      //   )
      // );
      p5.textSize(15);
      p5.text(
        this.id,
        this.pos[0] - p5.textWidth(this.id) / 2,
        this.pos[1] - 30
      );
    } else if (this.hover2) {
      p5.noStroke();
      p5.fill(GREEN_1_transparent);
      p5.circle(this.pos[0], this.pos[1], 2 * this.r2);
    }

    if (!p5.mouseIsPressed) {
      this.pos = vectorAdd(this.pos, this.vel);
      this.vel = vectorAdd(this.vel, this.acc);
    }
  }
}

export class Edge {
  node1: Node;
  node2: Node;
  highlighted: boolean;
  weight: number;
  arrowSize: number;

  constructor(a: Node, b: Node) {
    this.node1 = a;
    this.node2 = b;

    // styling stuff
    this.weight = 5;
    this.highlighted = false;
    this.arrowSize = 10;
  }

  drawArrow(p5: p5Types) {
    let base = this.node1.pos;
    let vec = vectorSub(this.node2.pos, base);

    p5.push();
    p5.translate(base[0], base[1]);
    p5.line(0, 0, vec[0], vec[1]);

    p5.rotate(vectorHeading(vec));
    let arrowSize = this.arrowSize;
    p5.translate(vectorMag(vec) - arrowSize - this.node2.r, 0);
    p5.triangle(0, arrowSize / 2, 0, -arrowSize / 2, arrowSize, 0);
    p5.pop();
  }

  draw(p5: p5Types) {
    p5.strokeWeight(this.weight);
    p5.stroke(LIGHT_2);
    p5.fill(LIGHT_2);

    // p5.line(
    //   this.node1.pos[0],
    //   this.node1.pos[1],
    //   this.node2.pos[0],
    //   this.node2.pos[1]
    // );
    // p5.circle(this.node2.pos[0], this.node2.pos[1], this.arrowSize);
    this.drawArrow(p5);

    if (this.highlighted) {
      p5.stroke(GREEN_1_transparent);
      p5.strokeWeight(this.weight * 5);

      p5.line(
        this.node1.pos[0],
        this.node1.pos[1],
        this.node2.pos[0],
        this.node2.pos[1]
      );
    }
  }
}

export interface P5State {
  initialized: boolean;
  scale: number;
  translate: number[];
  selectedOutMintIndex: number;
  selectedActionType: number;
  selectedNodeId: string;
  activeEdge: Edge | null;
  nodes: { [actionId: string]: Node };
}

export const dummyState: P5State = {
  initialized: false,
  scale: 1,
  translate: [0, 0],
  selectedOutMintIndex: 0,
  selectedActionType: 0,
  selectedNodeId: SOURCE_NAME,
  activeEdge: null,
  nodes: {
    SOURCE: new Node(SOURCE_NAME, [0, 0], true),
  },
};
